import { FlowerSpinner } from '@/shared/components/loader/flower-spinner';
import { Button } from '@/shared/components/shadcn/button';
import { Switch } from '@/shared/components/shadcn/switch';
import { cn } from '@/shared/utils';

export interface CreatorsResultsHeaderProps {
  filteredCount: number;
  selectedCount: number;
  totalCount: number;
  fullLoaded: boolean;
  isStopped?: boolean;
  isSearchReady: boolean;
  pageLoadPercent: number;
  initialProgress: number;
  hideSaved: boolean;
  hasMatchedFeatureFilter: boolean;
  setHideSaved: (value: boolean) => void;
  onSaveToListClick: (value: boolean) => void;
  onSaveAndExportClick: (value: boolean) => void;
}

export const CreatorsResultsHeader = ({
  filteredCount,
  selectedCount,
  totalCount,
  fullLoaded,
  isStopped = false,
  isSearchReady,
  pageLoadPercent,
  initialProgress,
  hideSaved,
  hasMatchedFeatureFilter,
  setHideSaved,
  onSaveToListClick,
  onSaveAndExportClick,
}: CreatorsResultsHeaderProps) => {
  const isSearching = !fullLoaded && !isStopped;
  const shouldShowFilteredCount = hideSaved || hasMatchedFeatureFilter;
  const showStoppedState = isStopped;
  const showInitialLoadingMessage = filteredCount === 0 && !fullLoaded && !showStoppedState;
  const showStreamingCountState = filteredCount > 0 && !fullLoaded && !showStoppedState;
  const showCompleteCountState = fullLoaded && !showStoppedState;
  const roundedInitialProgress = Math.min(initialProgress || 0, 100).toFixed(0);
  const initialLoadingMessage = isSearchReady
    ? 'Preparing results'
    : initialProgress < 20
      ? 'Understanding your prompt'
      : initialProgress < 50
        ? 'Building your search'
        : initialProgress < 80
          ? 'Searching creators'
          : 'Preparing results';

  return (
    <div className="bg-background relative h-[53px] px-4 py-2">
      <div className="flex h-full items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex min-w-0 items-center gap-2">
            {showInitialLoadingMessage && (
              <FlowerSpinner className="text-foreground size-4 shrink-0" />
            )}
            {showStreamingCountState && (
              <span className="relative size-3 shrink-0">
                <span className="bg-foreground/25 animate-brand-pulse-ring absolute inset-0 rounded-full" />
                <span className="bg-foreground animate-brand-pulse-core absolute inset-[3px] rounded-full" />
              </span>
            )}
            <div className="flex min-w-0 items-baseline gap-1.5 whitespace-nowrap">
              {showStoppedState && (
                <>
                  <span className="text-base leading-5 font-semibold">
                    {totalCount || filteredCount || 0}
                  </span>
                  <span className="text-muted-foreground text-sm leading-5">
                    creators found before stopping
                  </span>
                </>
              )}

              {showInitialLoadingMessage && (
                <>
                  <span className="text-base font-semibold">{initialLoadingMessage}</span>
                  <span className="text-muted-foreground ml-1 text-base">
                    {roundedInitialProgress}%
                  </span>
                </>
              )}

              {showStreamingCountState && shouldShowFilteredCount && (
                <>
                  <span className="text-base leading-5 font-semibold">{filteredCount || 0}</span>
                  <span className="text-muted-foreground text-sm leading-5">
                    of {totalCount || 0} creators found
                  </span>
                </>
              )}

              {showStreamingCountState && !shouldShowFilteredCount && (
                <>
                  <span className="text-base leading-5 font-semibold">{totalCount || 0}</span>
                  <span className="text-muted-foreground text-sm leading-5">creators found</span>
                </>
              )}

              {showCompleteCountState && shouldShowFilteredCount && (
                <>
                  <span className="text-base leading-5 font-semibold">{filteredCount || 0}</span>
                  <span className="text-muted-foreground text-sm leading-5">
                    of {totalCount || 0} creators
                  </span>
                </>
              )}

              {showCompleteCountState && !shouldShowFilteredCount && (
                <>
                  <span className="text-base leading-5 font-semibold">{totalCount || 0}</span>
                  <span className="text-muted-foreground text-sm leading-5">creators</span>
                </>
              )}

              {!showInitialLoadingMessage && selectedCount > 0 && (
                <span className="text-muted-foreground text-sm leading-5">
                  ({selectedCount} selected)
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <Switch
                checked={hideSaved}
                onCheckedChange={(value) => {
                  setHideSaved(value);
                }}
              />
              <label
                className="cursor-pointer text-sm font-medium whitespace-nowrap"
                onClick={() => {
                  setHideSaved(!hideSaved);
                }}
              >
                Hide saved
              </label>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => {
                onSaveToListClick(true);
              }}
              disabled={selectedCount === 0}
              variant="outline"
            >
              Save to list
            </Button>
            <Button
              size="sm"
              onClick={() => {
                onSaveAndExportClick(true);
              }}
              disabled={selectedCount === 0}
            >
              Save and Export
            </Button>
          </div>
        </div>
      </div>
      <div className="bg-border pointer-events-none absolute right-0 bottom-0 left-0 h-px" />
      <div
        className={cn(
          'bg-foreground pointer-events-none absolute bottom-0 left-0 h-px opacity-0',
          isSearching && 'opacity-100',
        )}
        style={{
          width: `${pageLoadPercent}%`,
          transition:
            'width 0.9s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.9s cubic-bezier(0.4, 0, 0.2, 1)',
          opacity: isSearching ? 1 : 0,
        }}
      />
    </div>
  );
};
